
import Image from "aldoo-ra/Image"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import MenuGroup from "aldoo-ra/MenuGroup"
import Copyright from "../Copyright"
import PoweredBy from "../PoweredBy"
import { navigate } from "vike/client/router"
import { usePageContext } from "vike-react/usePageContext"
import PubSub from "pubsub-js"

// Reads an "open" parameter off a menu item's link (e.g. open: "customer-support")
// so footer items can trigger a PubSub-driven panel instead of navigating.
function getOpenParam(item) {
  const linkParams = item.link?.[0]?.content?.parameters || item.link?.[0]?.parameters
  if (linkParams?.open) return linkParams.open
  const p = item.parameters
  if (!p) return null
  if (Array.isArray(p)) return p.find((e) => e.key === "open")?.value || null
  return p.open || null
}

function scrollToId(id, currentPath) {
  if (currentPath !== "/") {
    navigate("/")
    const start = Date.now()
    const poll = setInterval(() => {
      const el = document.getElementById(id)
      if (el) {
        clearInterval(poll)
        el.scrollIntoView({ behavior: "smooth" })
      } else if (Date.now() - start > 3000) {
        clearInterval(poll)
      }
    }, 50)
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })
  }
}


export default function FooterDesktop({ onOpenContactSales }) {
  const texts = TextBundle({ id: "transfer_slogan" })
  const pageContext = usePageContext()
  const currentPath = pageContext?.urlPathname || (typeof window !== "undefined" ? window.location.pathname : "/")

  const itemText = "text-white/70 hover:text-white"
  const labelText = "text-white"

  return (
    <footer className="relative bg-footer pt-16 pb-8">
      <div className="w-full max-w-w1_xl mx-auto px-px_sm md:px-px_md">
        {/* Top: logo + link groups */}
        <div className="flex flex-row items-start justify-between gap-10 mb-12">
          {/* Brand and stores column */}
          <div>
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault()
                navigate("/")
              }}
              className="flex items-center gap-3 shrink-0 mt-3"
            >
              <Image
                id="aldoo_transfer_logo_icon"
                className="h-11 w-auto object-contain"
              />

              <div className="flex flex-col mb-2">
                <span className="text-text font-bold text-2xl tracking-tight">
                  Aldoo <span className="text-primary">Transfer</span>
                </span>

                <Typography 
                  variant="txt"
                  className="text-text leading-none"
                >
                  {texts?.logo_slogan}
                </Typography>
              </div>
            </a>

            <MenuGroup
              renderAs={"vlist"}
              name="StoreButtons"
              itemsAsLinks={false}
              styleOverrides={{
                root_item: "py-0",
                menu_column: "hidden",
                menu_item: "px-0 mb-4"
              }}
              
              menuItemRender={(item) => {
                const url = item.link?.[0]?.url
                return (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="w-[180px]" style={{ aspectRatio: "220/65" }}>
                      <img
                        src={item.image}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </a>
                )
              }}
            />
          </div>

          {/* Link groups */}
          <div className="flex items-start justify-end gap-10 md:gap-16 lg:gap-24 xl:gap-[140px] mt-5">
            {/* Product Group */}
            <MenuGroup
              renderAs={"vlist"}
              name="FooterGroupProduct"
              itemsAsLinks={false}
              styleOverrides={{
                menu_column: "hidden",
                menu_item: "px-0 my-4"
              }}
              rootItemRender={(item) => (
                <Typography variant="footer_group_label" className={labelText}>
                  {item.label}
                </Typography>
              )}
              menuItemRender={(item) => {
                const url = item.link?.[0]?.url
                const isHashNav = url?.startsWith("#") || url?.startsWith("/#")
                return (
                  <a
                    href={url}
                    onClick={(e) => {
                      if (isHashNav) {
                        e.preventDefault()
                        const id = url.split("#")[1]
                        scrollToId(id, currentPath)
                      }
                    }}
                  >
                    <Typography variant="footer_item" className={itemText}>
                      {item.label}
                    </Typography>
                  </a>
                )
              }}
            />

            {/* Company Group */}
            <MenuGroup
              renderAs={"vlist"}
              name="FooterGroupCompany"
              itemsAsLinks={false}
              styleOverrides={{
                menu_column: "hidden",
                menu_item: "px-0 my-4"
              }}
              rootItemRender={(item) => (
                <Typography variant="footer_group_label" className={labelText}>
                  {item.label}
                </Typography>
              )}
              menuItemRender={(item) => {
                const url = item.link?.[0]?.url
                const isExternal = url?.startsWith("http://") || url?.startsWith("https://")
                return (
                  <a
                    href={url}
                    {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    <Typography variant="footer_item" className={itemText}>
                      {item.label.startsWith("Aldoo ")
                        ? <><strong>Aldoo</strong>{item.label.slice(5)}</>
                        : item.label}
                    </Typography>
                  </a>
                )
              }}
            />

            {/* Support Group */}
            <MenuGroup
              renderAs={"vlist"}
              name="FooterGroupSupport"
              itemsAsLinks={false}
              styleOverrides={{
                menu_column: "hidden",
                menu_item: "px-0 my-4"
              }}
              rootItemRender={(item) => (
                <Typography variant="footer_group_label" className={labelText}>
                  {item.label}
                </Typography>
              )}
              menuItemRender={(item) => {
                const url = item.link?.[0]?.url
                // PubSub-driven panel (e.g. Customer Support).
                const openParam = getOpenParam(item)
                if (openParam) {
                  return (
                    <button onClick={() => PubSub.publish(`open:${openParam}`)}>
                      <Typography variant="footer_item" className={itemText}>{item.label}</Typography>
                    </button>
                  )
                }
                // Contact Sales form.
                if (url && /#contact/.test(url)) {
                  return (
                    <button onClick={onOpenContactSales}>
                      <Typography variant="footer_item" className={itemText}>{item.label}</Typography>
                    </button>
                  )
                }
                const isHashNav = url?.startsWith("#") || url?.startsWith("/#")
                return (
                  <a
                    href={url}
                    onClick={(e) => {
                      if (isHashNav) {
                        e.preventDefault()
                        const id = url.split("#")[1]
                        scrollToId(id, currentPath)
                      }
                    }}
                  >
                    <Typography variant="footer_item" className={itemText}>
                      {item.label}
                    </Typography>
                  </a>
                )
              }}
            />
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-bordered mb-8" />

        {/* Bottom row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <Copyright />
          <PoweredBy />
        </div>
      </div>
    </footer>
  )
}
